import { useCallback, useEffect, useRef, useState } from "react";

/* ────────────────────────────────────────────────────────────────────────────
 * Toast — aviso flotante abajo-centro (verde si ok, rojo si error).
 * Uso:
 *   const { toast, showToast } = useToast();
 *   ...
 *   showToast("✓ Guardado");            // ok (verde)
 *   showToast("Error: " + e.message, false); // error (rojo)
 *   ...
 *   <ToastView toast={toast} />
 * ──────────────────────────────────────────────────────────────────────────── */

export type ToastState = { msg: string; ok: boolean } | null;

export function useToast() {
  const [toast, setToast] = useState<ToastState>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string, ok: boolean = true) => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ msg, ok });
    timer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { toast, showToast };
}

export function ToastView({ toast }: { toast: ToastState }) {
  if (!toast) return null;
  const accent = toast.ok ? "#10b981" : "#ef4444";
  return (
    <div style={{
      position: "fixed",
      bottom: 24,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2000,
      maxWidth: "min(480px, calc(100vw - 32px))",
      background: "var(--clr-modal-solid, var(--clr-modal-bg, #fff))",
      border: "1px solid var(--clr-border)",
      borderRadius: 999,
      padding: "0.7rem 1.25rem",
      color: "var(--clr-text)",
      fontSize: "0.85rem",
      fontWeight: 500,
      boxShadow: "0 12px 40px -12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.6)",
      backdropFilter: "var(--clr-blur, blur(8px))",
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: accent, flexShrink: 0, display: "inline-block" }} />
      <span>{toast.msg}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * ConfirmDialog — modal de confirmación accesible (sustituye a confirm()).
 * ──────────────────────────────────────────────────────────────────────────── */

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Eliminar",
  danger = true,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmBg = danger ? "#ef4444" : "var(--clr-accent, #0891b2)";

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 1500,
        background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--clr-modal-solid, var(--clr-modal-bg, #fff))",
          border: "1px solid var(--clr-border)",
          borderRadius: 22,
          padding: "1.6rem",
          width: "min(400px, 100%)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
          color: "var(--clr-text)",
        }}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: "1.05rem", fontWeight: 700 }}>{title}</h3>
        {message && (
          <p style={{ margin: "0 0 1.25rem", fontSize: "0.85rem", color: "var(--clr-text3)", lineHeight: 1.5 }}>
            {message}
          </p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: message ? 0 : "1.25rem" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: 44, padding: "10px 18px",
              background: "var(--clr-surface2, transparent)",
              color: "var(--clr-text2)",
              border: "1px solid var(--clr-border)",
              borderRadius: 999, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            style={{
              minHeight: 44, padding: "10px 18px",
              background: confirmBg, color: "#fff",
              border: "none", borderRadius: 999,
              fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
